import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: {
          logo: 'Composition Marking',
          features: 'Features',
          about: 'About',
          contact: 'Contact',
          heroTitle: 'Streamline Your Composition Grading',
          heroSubtitle: 'An intelligent platform that helps educators grade compositions efficiently while providing detailed feedback to students.',
          getStarted: 'Get Started',
          keyFeatures: 'Key Features',
          smartGrading: 'Smart Grading',
          smartGradingDesc: 'AI-powered grading assistance to help you evaluate compositions quickly and consistently.',
          detailedFeedback: 'Detailed Feedback',
          detailedFeedbackDesc: 'Generate comprehensive feedback that helps students understand their strengths and areas for improvement.',
          progressTracking: 'Progress Tracking',
          progressTrackingDesc: 'Monitor student progress over time with detailed analytics and performance metrics.',
          collaborativeReview: 'Collaborative Review',
          collaborativeReviewDesc: 'Enable peer review and collaborative feedback sessions to enhance learning outcomes.',
          aboutTitle: 'Our Mission',
          aboutContent: `As native Chinese-speaking children growing up in an English-dominant environment, many face unique challenges in maintaining and developing their Chinese language skills. The constant exposure to English in daily life can make it difficult to achieve proficiency in Chinese composition writing.

Our platform is specifically designed to address these challenges by providing:
- AI-powered grading assistance tailored for Chinese composition
- Detailed feedback in both Chinese and English
- Progress tracking to monitor improvement
- Collaborative learning opportunities with peers

We believe that with the right tools and support, every child can excel in Chinese composition writing, regardless of their daily language environment.`,
          close: 'Close',
          pricing: 'Pricing',
          pricingTitle: 'Simple, Transparent Pricing',
          freePlan: 'Free',
          proPlan: 'Professional',
          enterprisePlan: 'Enterprise',
          month: 'month',
          free: 'Free',
          custom: 'Custom',
          contactUs: 'Contact Us',
          freeFeature1: 'Basic score only (0-100)',
          freeFeature2: 'One-time marking per composition',
          freeFeature3: 'No progress tracking',
          proFeature1: 'Detailed feedback and comments',
          proFeature2: 'Inline suggestions and corrections',
          proFeature3: 'Progress tracking for same topic',
          proFeature4: 'Store up to 100 compositions',
          enterpriseFeature1: 'Unlimited compositions',
          enterpriseFeature2: 'Advanced feedback and analysis',
          enterpriseFeature3: 'Custom feedback templates',
          enterpriseFeature4: 'Detailed progress analytics',
          enterpriseFeature5: 'Priority support',
          uploadTitle: 'Upload Your Composition',
          uploadDescription: 'Upload a scanned PDF or image of your composition to get instant AI-powered marking',
          uploadingTitle: 'Uploading Your Composition',
          uploadingDescription: 'Please wait while we upload your composition...',
          processingTitle: 'Processing Your Composition',
          processingDescription: 'We\'re processing your composition with AI...',
          markingTitle: 'Marking Your Composition',
          markingDescription: 'We\'re marking your composition...',
          markingCompleteTitle: 'Marking Complete',
          markingCompleteDescription: 'Review your marking results and feedback',
          editTitle: 'Edit Your Composition',
          editDescription: 'Edit your composition before submitting for marking',
          selectAndUpload: 'Select and Upload File',
          uploading: 'Uploading...',
          processing: 'Processing...',
          processingStatus: 'Processing text with AI...',
          processingComplete: 'Text processing complete!',
          processingError: 'Text processing failed',
          invalidResponseError: 'Invalid response format from AI',
          viewFile: 'View File',
          yourWriting: 'Your Writing',
          editPlaceholder: 'Edit your writing here...',
          submitForMarking: 'Submit for Marking',
          marking: 'Marking...',
          markingStatus: 'Marking composition...',
          markingComplete: 'Marking complete!',
          markingError: 'Marking failed',
          markingResult: 'Marking Result',
          score: 'Score',
          feedback: 'Feedback',
          editedVersion: 'Edited Version',
          comment: 'Comment',
          recommendation: 'Recommendation',
          originalText: 'Original Text',
          editedText: 'Edited Text',
          compositionTopic: 'Composition Topic',
          noTopic: 'No topic found',
          grammarCorrections: 'Grammar Corrections',
          scoreBreakdown: {
            title: 'Score Breakdown',
            content: {
              title: 'Content (40 points)',
              theme: 'Theme',
              depth: 'Depth',
              structure: 'Structure'
            },
            language: {
              title: 'Language (40 points)',
              vocabulary: 'Vocabulary',
              sentence: 'Sentence',
              rhetoric: 'Rhetoric'
            },
            grammar: {
              title: 'Grammar (20 points)',
              grammar: 'Grammar',
              punctuation: 'Punctuation'
            }
          },
          grade: 'HKDSE Grade'
        }
      },
      zh: {
        translation: {
          logo: '作文評分系統',
          features: '功能',
          about: '關於',
          contact: '聯絡',
          heroTitle: '簡化您的作文評分流程',
          heroSubtitle: '一個智能平台，幫助教育工作者高效評分作文，同時為學生提供詳細的反饋。',
          getStarted: '開始使用',
          keyFeatures: '主要功能',
          smartGrading: '智能評分',
          smartGradingDesc: 'AI 輔助評分，幫助您快速且一致地評估作文。',
          detailedFeedback: '詳細反饋',
          detailedFeedbackDesc: '生成全面的反饋，幫助學生了解他們的優勢和需要改進的地方。',
          progressTracking: '進度追蹤',
          progressTrackingDesc: '通過詳細的分析和表現指標，監控學生的學習進度。',
          collaborativeReview: '協作評審',
          collaborativeReviewDesc: '啟用同儕評審和協作反饋，提升學習效果。',
          aboutTitle: '我們的使命',
          aboutContent: `在英語環境中成長的華裔兒童，在保持和發展中文能力方面面臨著獨特的挑戰。日常生活中持續接觸英語，使得他們在中文寫作方面難以達到熟練程度。

我們的平台專門針對這些挑戰而設計，提供：
- 專為中文作文設計的 AI 輔助評分
- 中英雙語的詳細反饋
- 追蹤進步的進度監控
- 與同儕的協作學習機會

我們相信，只要有適當的工具和支持，每個孩子都能在中文寫作方面取得優異成績，無論他們的日常語言環境如何。`,
          close: '關閉',
          pricing: '價格',
          pricingTitle: '簡單透明的價格方案',
          freePlan: '免費版',
          proPlan: '專業版',
          enterprisePlan: '企業版',
          month: '月',
          free: '免費',
          custom: '客製化',
          contactUs: '聯絡我們',
          freeFeature1: '基本分數（0-100分）',
          freeFeature2: '每篇作文限評分一次',
          freeFeature3: '無進度追蹤',
          proFeature1: '詳細反饋和評語',
          proFeature2: '行內建議和修改',
          proFeature3: '相同主題的進度追蹤',
          proFeature4: '最多儲存 100 篇作文',
          enterpriseFeature1: '無限篇作文',
          enterpriseFeature2: '進階反饋和分析',
          enterpriseFeature3: '自訂回饋範本',
          enterpriseFeature4: '詳細進度分析',
          enterpriseFeature5: '優先支援',
          uploadTitle: '上傳作文',
          uploadDescription: '上傳作文的掃描PDF或圖片，獲取即時AI評分',
          uploadingTitle: '正在上傳作文',
          uploadingDescription: '請稍候，正在上傳您的作文...',
          processingTitle: '正在處理作文',
          processingDescription: '正在使用AI處理您的作文...',
          markingTitle: '正在評分作文',
          markingDescription: '正在為您的作文評分...',
          markingCompleteTitle: '評分完成',
          markingCompleteDescription: '查看評分結果和反饋',
          editTitle: '編輯作文',
          editDescription: '在提交評分前編輯您的作文',
          selectAndUpload: '選擇並上傳文件',
          uploading: '上傳中...',
          processing: '處理中...',
          processingStatus: '正在使用AI處理文本...',
          processingComplete: '文本處理完成！',
          processingError: '文本處理失敗',
          invalidResponseError: 'AI響應格式無效',
          viewFile: '查看文件',
          yourWriting: '您的作文',
          editPlaceholder: '在此編輯您的作文...',
          submitForMarking: '提交評分',
          marking: '評分中...',
          markingStatus: '正在評分作文...',
          markingComplete: '評分完成！',
          markingError: '評分失敗',
          markingResult: '評分結果',
          score: '分數',
          feedback: '反饋',
          editedVersion: '修改版本',
          comment: '評語',
          recommendation: '建議',
          originalText: '原文',
          editedText: '修改後',
          compositionTopic: '作文題目',
          noTopic: '未找到題目',
          grammarCorrections: '文法修改',
          scoreBreakdown: {
            title: '評分細項',
            content: {
              title: '內容 (40分)',
              theme: '主題',
              depth: '深度',
              structure: '結構'
            },
            language: {
              title: '語言表達 (40分)',
              vocabulary: '詞彙',
              sentence: '句式',
              rhetoric: '修辭'
            },
            grammar: {
              title: '文法 (20分)',
              grammar: '語法',
              punctuation: '標點'
            }
          },
          grade: '香港文憑試等級'
        }
      }
    },
    lng: 'zh',
    fallbackLng: 'zh',
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    interpolation: {
      escapeValue: false
    }
  });

export default i18n; 